import { Calendar } from 'phosphor-react';
import React, { useRef } from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { twMerge } from 'tailwind-merge';
import { isHTML, toInlineHTML } from '../../../../utils/helpers';

// The rest of the app formats dates with moment tokens (e.g. 'MM/DD/YYYY'),
// but react-datepicker parses/formats using date-fns tokens (e.g. 'MM/dd/yyyy').
// Convert so callers can keep passing the moment-style format they already use.
const toDateFnsFormat = (format: string) =>
  format.replace(/YYYY/g, 'yyyy').replace(/YY/g, 'yy').replace(/DD/g, 'dd').replace(/D/g, 'd');

// Strips everything but digits and re-inserts the "/" separators as the user
// types, so "08312026" becomes "08/31/2026" instead of showing raw digits.
const maskDateInput = (raw: string, monthYearOnly: boolean) => {
  const digits = raw.replace(/\D/g, '').slice(0, monthYearOnly ? 6 : 8);
  if (monthYearOnly) {
    return digits.length <= 2 ? digits : `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

// react-datepicker formats its dates with date-fns, which throws "Invalid time value" on an
// Invalid Date. Callers build dates from saved answers (`new Date(value)`), and a value that does
// not parse — e.g. typed into the field before it was a date field — would crash the whole page.
const toValidDate = (date: Date | null | undefined) =>
  date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;

/**
 * Turns a saved date answer into a Date for the picker. Answers are saved as MM-DD-YYYY, which
 * `new Date()` only parses in some browsers — Safari returns an Invalid Date — so parse the
 * formats the app writes explicitly and fall back to `new Date()` for anything else
 * (ISO strings, `toDateString()` output).
 */
export const parseDateValue = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return toValidDate(value);
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const parsed = moment(
    value,
    ['MM-DD-YYYY', 'MM/DD/YYYY', 'MM-YYYY', 'MM/YYYY', moment.ISO_8601],
    true
  );
  return parsed.isValid() ? parsed.toDate() : toValidDate(new Date(value));
};

const MaskedDateInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { monthYearOnly?: boolean }
>(({ monthYearOnly = false, onChange, ...rest }, ref) => {
  const localRef = useRef<HTMLInputElement | null>(null);

  const setRefs = (node: HTMLInputElement | null) => {
    localRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = maskDateInput(e.target.value, monthYearOnly);
    e.target.value = masked;
    onChange?.(e);
    requestAnimationFrame(() => {
      localRef.current?.setSelectionRange(masked.length, masked.length);
    });
  };

  return (
    <input
      {...rest}
      ref={setRefs}
      onChange={handleChange}
      inputMode="numeric"
      maxLength={monthYearOnly ? 7 : 10}
    />
  );
});
MaskedDateInput.displayName = 'MaskedDateInput';

const AppDatePicker = ({
  selectedDate,
  onDateSelected,
  wrapperClasses = '',
  pickerClasses = '',
  errorText = '',
  label = '',
  labelTextClassName = '',
  labelSubtext = '',
  isRequired = false,
  minumumDate = null,
  maximumDate = null,
  dateFormat = 'MM/DD/YYYY',
  isDisabled = false,
  showMonthYearPicker = false,
}: {
  selectedDate: Date | null | undefined;
  onDateSelected: (val: Date | null | undefined) => void;
  wrapperClasses?: string;
  pickerClasses?: string;
  errorText?: string;
  label?: string;
  labelTextClassName?: string;
  labelSubtext?: string;
  isRequired?: boolean;
  minumumDate?: Date | null;
  maximumDate?: Date | null;
  dateFormat?: string;
  isDisabled?: boolean;
  showMonthYearPicker?: boolean;
}) => {
  const displayFormat = toDateFnsFormat(showMonthYearPicker ? 'MM/YYYY' : dateFormat);
  const validSelectedDate = toValidDate(selectedDate);

  return (
    <div className="w-full flex flex-col relative">
      {(label || isRequired) && (
        <p className={twMerge('text-xs mb-0.5 whitespace-nowrap font-medium', labelTextClassName)}>
          {isRequired && <span className="text-red-500 mr-0.5">*</span>}
          {label}
        </p>
      )}
      {labelSubtext &&
        (isHTML(labelSubtext) ? (
          <p
            className="text-2xs mb-0.5 text-left GREY_500-CLR"
            dangerouslySetInnerHTML={{ __html: toInlineHTML(labelSubtext) }}
          />
        ) : (
          <p className="text-2xs mb-0.5 text-left GREY_500-CLR">{labelSubtext}</p>
        ))}

      <div className={twMerge('w-full flex flex-col justify-center', wrapperClasses)}>
        <div
          className={twMerge(
            'flex items-center border px-1.5 py-2 rounded-lg bg-white transition',
            isDisabled && 'opacity-60 GREY_200-BG cursor-not-allowed'
          )}
          style={{
            borderColor: errorText ? '#F04438' : '#E4EAF1',
          }}>
          <Calendar className="text-slate-500 mr-2 shrink-0" size={23} />
          <DatePicker
            className={twMerge(
              'w-full text-sm outline-none bg-transparent',
              isDisabled ? 'text-slate-500 cursor-not-allowed' : 'cursor-pointer',
              pickerClasses
            )}
            wrapperClassName="w-full"
            placeholderText={showMonthYearPicker ? 'Select month & year' : 'Select date'}
            selected={validSelectedDate}
            onChange={(val) => onDateSelected(val)}
            dateFormat={displayFormat}
            showMonthYearPicker={showMonthYearPicker}
            minDate={toValidDate(minumumDate)}
            maxDate={toValidDate(maximumDate)}
            disabled={isDisabled}
            autoComplete="off"
            customInput={<MaskedDateInput monthYearOnly={showMonthYearPicker} />}
            showMonthDropdown={!showMonthYearPicker}
            showYearDropdown
            dropdownMode="select"
            yearDropdownItemNumber={100}
            isClearable={!isDisabled && !!validSelectedDate}
            todayButton="Today"
            portalId="app-date-picker-portal"
          />
        </div>

        {errorText && <p className="text-xs mt-0.5 text-red-600">{errorText}</p>}
      </div>
    </div>
  );
};

export default AppDatePicker;
